import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { Truck, MapPin, Package, Users, ArrowRight, CheckCircle2, Smartphone, Info, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import MapLocationPicker from "@/components/MapLocationPicker";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";



const CargoBookingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [formData, setFormData] = useState({
    pickup_address: "",
    dropoff_address: "",
    item_type: "",
    item_size: "",
    vehicle_type: "",
    has_stairs: false,
    has_elevator: false,
    helpers_needed: 0,
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    notes: ""
  });
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);

  // Pre-fill form with user data when logged in
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email
      }));
    }
  }, [user]);

  const itemTypes = [
    "Furniture",
    "Appliances",
    "Electronics",
    "Moving Boxes",
    "Construction Materials",
    "Other"
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getEstimate = async () => {
    if (!formData.pickup_address || !formData.dropoff_address || !formData.item_type || !formData.item_size || !formData.vehicle_type) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid pickup location on the map");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        pickup_lat: pickupCoords?.latitude,
        pickup_lng: pickupCoords?.longitude,
        dropoff_lat: dropoffCoords?.latitude,
        dropoff_lng: dropoffCoords?.longitude,
      };
      const res = await axios.post(`${API}/cargo/estimate`, payload);
      setEstimate(res.data);
      setStep(2);
    } catch {
      toast.error("Failed to get estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async () => {
    setLoading(true);
    try {
      const payload = { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude };
      const response = await axios.post(`${API}/cargo/booking`, payload);
      setBookingId(response.data.id);
      setStep(3);
      toast.success("Booking submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    setPaymentLoading(true);
    try {
      // Step 1: Create the booking
      const bookingPayload = { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude };
      const bookingResponse = await axios.post(`${API}/cargo/booking`, bookingPayload);
      const booking = bookingResponse.data;
      const paymentAmount = estimate.max_price;

      // Service request data for dispatch
      const serviceData = {
        booking_id: booking.id,
        booking_type: "cargo",
        amount: paymentAmount,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        service_type: "cargo_delivery",
        pickup_address: formData.pickup_address,
        dropoff_address: formData.dropoff_address,
        pickup_coords: pickupCoords,
        dropoff_coords: dropoffCoords,
        vehicle_type: formData.vehicle_type,
      };

      if (selectedCardId && selectedCardId !== "new") {
        // Step 2a: Authorize hold with saved card
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });

        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your driver...");
          setBookingId(booking.id);
          setStep(3);
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setPaymentLoading(false);
      } else {
        // Step 2b: Redirect to Stripe checkout with pre-auth hold (new card)
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Authorization error:", error);
      const errorMsg = getErrorMessage(error, "Payment authorization failed. Please try another payment method.");
      toast.error(errorMsg);
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="cargo-booking-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_stackcart-app/artifacts/893edl5l_Home-Welcome-1.jpg')" }}
        ></div>
        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/90 via-emerald-900/85 to-emerald-950/80"></div>
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <div className="w-16 h-16 bg-lime-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <Truck className="w-8 h-8 text-emerald-950" />
            </div>
            <h1 className="heading-2 text-white mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>Cargo Delivery</h1>
            <p className="text-lg md:text-xl leading-relaxed text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
              Move furniture, appliances, and large items with our network of professional drivers and helpers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {/* Delivery Details */}
          {step === 1 && (
          <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-8"
              data-testid="cargo-step-1"
            >
              <h2 className="heading-3 text-emerald-950 mb-6">Delivery Details</h2>

              <div className="space-y-6">
                {/* Addresses with Map */}
                <MapLocationPicker
                  pickupAddress={formData.pickup_address}
                  dropoffAddress={formData.dropoff_address}
                  onPickupChange={(val) => handleInputChange("pickup_address", val)}
                  onDropoffChange={(val) => handleInputChange("dropoff_address", val)}
                  onPickupCoordsChange={setPickupCoords}
                  onDropoffCoordsChange={setDropoffCoords}
                  pickupPlaceholder="123 Main St, Toronto"
                  dropoffPlaceholder="456 Oak Ave, Mississauga"
                  serviceType="cargo_delivery"
                />

                {/* Item Details */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-emerald-950 font-medium mb-2 block">Item Type *</Label>
                    <Select
                      value={formData.item_type}
                      onValueChange={(value) => handleInputChange("item_type", value)}
                    >
                      <SelectTrigger className="input-style" data-testid="item-type-select">
                        <SelectValue placeholder="Select item type" />
                      </SelectTrigger>
                      <SelectContent>
                        {itemTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-emerald-950 font-medium mb-2 block">Item Size *</Label>
                    <Select
                      value={formData.item_size}
                      onValueChange={(value) => handleInputChange("item_size", value)}
                    >
                      <SelectTrigger className="input-style" data-testid="item-size-select">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small (fits in car trunk)</SelectItem>
                        <SelectItem value="medium">Medium (requires SUV/van)</SelectItem>
                        <SelectItem value="large">Large (requires truck)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Vehicle Type */}
                <div>
                  <Label className="text-emerald-950 font-medium mb-3 block">Vehicle Type *</Label>
                  <p className="text-xs text-stone-500 mb-3">Select the vehicle size needed for your cargo. You'll be matched with professionals who have this vehicle.</p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "small", label: "Pickup", desc: "Pickup Truck", capacity: "Up to 1,000 lbs",
                        icon: <svg viewBox="0 0 64 32" className="w-12 h-6 mx-auto mb-2"><path d="M8 24h4a4 4 0 008 0h12a4 4 0 008 0h8v-6l-6-8H30l-2-4H8a4 4 0 00-4 4v10a4 4 0 004 4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="16" cy="24" r="3" fill="currentColor"/><circle cx="44" cy="24" r="3" fill="currentColor"/><line x1="30" y1="10" x2="30" y2="18" stroke="currentColor" strokeWidth="1.5"/></svg>
                      },
                      { value: "medium", label: "Cargo Van", desc: "Enclosed Van", capacity: "Up to 3,000 lbs",
                        icon: <svg viewBox="0 0 64 32" className="w-12 h-6 mx-auto mb-2"><path d="M6 24h4a4 4 0 008 0h20a4 4 0 008 0h6V8a4 4 0 00-4-4H10a4 4 0 00-4 4v16z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="14" cy="24" r="3" fill="currentColor"/><circle cx="42" cy="24" r="3" fill="currentColor"/><rect x="38" y="8" width="10" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>
                      },
                      { value: "large", label: "Truck", desc: "Box Truck / Flatbed", capacity: "Up to 10,000 lbs",
                        icon: <svg viewBox="0 0 64 32" className="w-14 h-7 mx-auto mb-2"><path d="M4 24h2a4 4 0 008 0h6V6a2 2 0 012-2h20a2 2 0 012 2v18h2a4 4 0 008 0h4v-8l-6-10h-8V6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="10" cy="24" r="3" fill="currentColor"/><circle cx="50" cy="24" r="3" fill="currentColor"/><rect x="4" y="6" width="28" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>
                      },
                    ].map((v) => (
                      <button
                        key={v.value}
                        type="button"
                        onClick={() => handleInputChange("vehicle_type", v.value)}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${formData.vehicle_type === v.value ? "border-lime-500 bg-lime-50" : "border-stone-200 hover:border-stone-300"}`}
                        data-testid={`vehicle-type-${v.value}`}
                      >
                        <div className={formData.vehicle_type === v.value ? "text-lime-600" : "text-stone-400"}>
                          {v.icon}
                        </div>
                        <div className="font-semibold text-sm text-emerald-950">{v.label}</div>
                        <div className="text-xs text-stone-500">{v.desc}</div>
                        <div className="text-[10px] text-stone-400 mt-1">{v.capacity}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  className="btn-primary w-full"
                  onClick={getEstimate}
                  disabled={loading}
                  data-testid="get-estimate-btn"
                >
                  {loading ? "Getting Estimate..." : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
            )}

            {/* Step 2: Payment */}
            {step === 2 && estimate && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
                <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
                <div className="bg-lime-50 border border-lime-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-stone-600">Estimated Price</p>
                  <p className="text-3xl font-bold text-lime-700">${estimate.max_price.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
                </div>
                <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
                <Button className="w-full bg-lime-500 hover:bg-lime-600 text-emerald-950 rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                  {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Driver <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </motion.div>
            )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CargoBookingPage;
